import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth.js';

const RECAPTCHA_CONTAINER_ID = 'sfp-recaptcha';

export function Login() {
  const { signIn, signInWithGoogle, mfaChallenge, completeMfa, cancelMfa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [smsCode, setSmsCode] = useState('');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password, RECAPTCHA_CONTAINER_ID);
    } catch (err) {
      setError(prettifyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const onGoogle = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle(RECAPTCHA_CONTAINER_ID);
    } catch (err) {
      setError(prettifyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const onCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!smsCode.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await completeMfa(smsCode.trim());
      setSmsCode('');
    } catch (err) {
      setError(prettifyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FFFFFF] px-4">
      <div className="w-full max-w-sm rounded-2xl border border-stone-200 bg-white p-7 shadow-lg">
        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-[color:var(--sfp-primary)] text-base font-bold text-[color:var(--sfp-sidebar)]">
            ◆
          </div>
          <div>
            <div className="text-base font-semibold text-stone-900">SFP</div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500">
              Shulkin Family Partners
            </div>
          </div>
        </div>

        {mfaChallenge ? (
          /* ----------- MFA SMS code step ----------- */
          <form onSubmit={onCodeSubmit}>
            <div className="mb-3 rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600">
              {mfaChallenge.factor === 'totp' ? (
                <>Open your authenticator app and enter the current 6-digit code to finish signing in.</>
              ) : (
                <>
                  We sent a 6-digit code to the phone ending in{' '}
                  <strong>{mfaChallenge.hint}</strong>. Enter it to finish signing in.
                </>
              )}
            </div>
            <label className="mb-4 block text-sm">
              <span className="block pb-1 text-xs font-medium text-stone-600">
                {mfaChallenge.factor === 'totp' ? 'Authenticator code' : 'SMS code'}
              </span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                required
                placeholder="123456"
                className="w-full rounded border border-stone-300 px-3 py-2 text-center font-mono text-lg tracking-widest focus:border-stone-500 focus:outline-none"
                autoFocus
              />
            </label>
            {error && (
              <div className="mb-3 rounded border border-[#9C4A3A] bg-[#9C4A3A]/10 px-3 py-2 text-xs text-[#9C4A3A]">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="w-full rounded bg-[color:var(--sfp-primary)] py-2 text-sm font-medium text-white disabled:opacity-50"
              disabled={busy || smsCode.length !== 6}
            >
              {busy ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <button
              type="button"
              onClick={cancelMfa}
              className="mt-3 w-full text-center text-xs text-stone-500 hover:text-stone-800"
            >
              Cancel and start over
            </button>
          </form>
        ) : (
          /* ----------- Standard sign-in step ----------- */
          <form onSubmit={onSubmit}>
            <label className="mb-3 block text-sm">
              <span className="block pb-1 text-xs font-medium text-stone-600">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@shulkinfamilypartners.com"
                className="w-full rounded border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
                autoComplete="email"
                autoFocus
              />
            </label>
            <label className="mb-4 block text-sm">
              <span className="block pb-1 text-xs font-medium text-stone-600">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none"
                autoComplete="current-password"
              />
            </label>
            {error && (
              <div className="mb-3 rounded border border-[#9C4A3A] bg-[#9C4A3A]/10 px-3 py-2 text-xs text-[#9C4A3A]">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="w-full rounded bg-[color:var(--sfp-primary)] py-2 text-sm font-medium text-white disabled:opacity-50"
              disabled={busy}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-stone-200" />
              <span className="text-[10px] uppercase tracking-wider text-stone-400">or</span>
              <div className="h-px flex-1 bg-stone-200" />
            </div>

            <button
              type="button"
              onClick={onGoogle}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded border border-stone-300 bg-white py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
              </svg>
              Continue with Google
            </button>

            <p className="mt-4 text-center text-[11px] leading-relaxed text-stone-500">
              Access is by invitation only and requires SMS verification.
              <br />
              Ask an admin (jon@ or admin@) to add you.
            </p>
          </form>
        )}

        {/* Invisible reCAPTCHA for SMS sending (Identity Platform requirement) */}
        <div id={RECAPTCHA_CONTAINER_ID} />
      </div>
    </div>
  );
}

function prettifyAuthError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/wrong-password|invalid-credential/i.test(msg)) return 'Wrong email or password.';
  if (/user-not-found/i.test(msg)) return "We don't have an account for that email.";
  if (/too-many-requests/i.test(msg)) return 'Too many attempts. Try again in a few minutes.';
  if (/disabled/i.test(msg)) return 'This account has been disabled. Contact an admin.';
  if (/invalid-verification-code/i.test(msg)) return 'That code is wrong. Try again.';
  if (/code-expired/i.test(msg)) return 'That code expired. Cancel and sign in again to get a fresh code.';
  if (/operation-not-allowed/i.test(msg))
    return 'This sign-in method is not enabled. An admin must turn it on.';
  if (/account-exists-with-different-credential/i.test(msg))
    return 'An account with this email already exists using a password. Sign in with your password.';
  if (/popup-closed-by-user|cancelled-popup-request|popup-blocked/i.test(msg))
    return 'Google sign-in was cancelled or blocked. Allow popups and try again.';
  if (/unauthorized-domain/i.test(msg))
    return 'This domain is not authorized for sign-in. An admin must add it in Firebase Auth settings.';
  if (/not authorized for SFP|allowlist/i.test(msg))
    return 'This email is not on the SFP allowlist. Ask an admin to invite you.';
  return msg.replace(/^Firebase:\s*/, '').replace(/\s+\(auth\/[^)]+\)\.?$/, '');
}
